import { spacing } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type { CoreUITheme } from '@scality/core-ui/dist/style/theme';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { XXLargeGradientText } from '../../TextVariants';

/**
 * How the value reads, not what it means: the card paints a colour, the caller owns the policy
 * that picked it (a vitals threshold, an alert severity, a metric's own bounds).
 */
export type MetricCardStatus = 'headline' | 'neutral' | 'healthy' | 'warning' | 'critical';

export type MetricCardProps = {
  title: ReactNode;
  /** Already formatted. The card owns no unit, rounding or locale policy. */
  value: ReactNode;
  status?: MetricCardStatus;
  /** A small line under the value — a unit, a timespan, a trend. */
  caption?: ReactNode;
  /** Room under the value for a sparkline or a trend chart. */
  footer?: ReactNode;
};

const STATUS_TOKEN: Record<Exclude<MetricCardStatus, 'headline' | 'neutral'>, keyof CoreUITheme> = {
  healthy: 'statusHealthy',
  warning: 'statusWarning',
  critical: 'statusCritical',
};

const Card = styled.div`
  border-radius: ${spacing.f8};
  background-color: ${({ theme }) => theme.backgroundLevel3};
  padding: calc(${spacing.f8} + ${spacing.r8});
  display: flex;
  flex-direction: column;
  gap: ${spacing.r8};
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.2);
`;

/**
 * Two lines' worth of height whatever the title's length, so a row of cards keeps its values on
 * one baseline instead of stepping down under the longer titles.
 */
const CardTitle = styled.div`
  font-size: 1.2em;
  line-height: 1.2em;
  height: 2.4em;
  overflow: hidden;
  color: ${({ theme }) => theme.textPrimary};
`;

/**
 * `headline` keeps the brand gradient, for a number the card exists to show off (a vitals score in
 * good health). `neutral` reads as grey — the value needs no comment, so it must not compete with
 * the three that do. It keeps the clipped-background treatment on `buttonSecondary`, whose
 * brightened grey stays legible at 4em; a status paints a flat token instead, dropping the gradient
 * rather than leaving it clipped behind the glyphs.
 */
const Value = styled(XXLargeGradientText)<{ $status: MetricCardStatus }>`
  font-size: 4em;
  line-height: initial;
  white-space: nowrap;
  ${({ $status, theme }) => {
    if ($status === 'headline') return '';
    if ($status === 'neutral')
      return `
        background: ${theme.buttonSecondary};
        background-clip: text;
      `;
    return `
        background: none;
        color: ${theme[STATUS_TOKEN[$status]]};
      `;
  }}
`;

/**
 * One number, big, on a card — the shape the vitals dashboard made familiar, with nothing about
 * vitals left in it.
 *
 * `value` arrives formatted and `status` arrives decided, which is the whole point: the same card
 * serves a vitals score out of 100, a capacity in TB and an alert count, and none of those units
 * or thresholds live here.
 */
export const MetricCard = ({ title, value, status = 'neutral', caption, footer }: MetricCardProps) => (
  <Card>
    <CardTitle>{title}</CardTitle>
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={spacing.f4}>
      <Box display="flex" alignItems="center" justifyContent="center" height="80px">
        <Value $status={status}>{value}</Value>
      </Box>
      {caption}
    </Box>
    {footer}
  </Card>
);
